import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { RunState } from '../run/types';

const DATABASE_NAME = 'gauntle-unlimited';
const DATABASE_VERSION = 1;
const ACTIVE_RUN_KEY = 'active-run-id';

interface GauntleDatabase extends DBSchema {
  runs: {
    key: string;
    value: RunState;
    indexes: { 'by-updated': number; 'by-phase': string };
  };
  metadata: {
    key: string;
    value: string;
  };
}

export interface RunRepository {
  save(run: RunState): Promise<void>;
  loadActive(): Promise<RunState | null>;
  clearActive(): Promise<void>;
  delete(runId: string): Promise<void>;
  listCompleted(): Promise<RunState[]>;
  deleteAll(): Promise<void>;
}

let databasePromise: Promise<IDBPDatabase<GauntleDatabase>> | null = null;

function getDatabase(): Promise<IDBPDatabase<GauntleDatabase>> {
  if (!databasePromise) {
    databasePromise = openDB<GauntleDatabase>(DATABASE_NAME, DATABASE_VERSION, {
      upgrade(database) {
        const runs = database.createObjectStore('runs', { keyPath: 'id' });
        runs.createIndex('by-updated', 'updatedAt');
        runs.createIndex('by-phase', 'phase');
        database.createObjectStore('metadata');
      },
    });
  }
  return databasePromise;
}

export const indexedDbRunRepository: RunRepository = {
  async save(run) {
    const database = await getDatabase();
    const transaction = database.transaction(['runs', 'metadata'], 'readwrite');
    await Promise.all([
      transaction.objectStore('runs').put(run),
      run.phase === 'results'
        ? transaction.objectStore('metadata').delete(ACTIVE_RUN_KEY)
        : transaction.objectStore('metadata').put(run.id, ACTIVE_RUN_KEY),
      transaction.done,
    ]);
  },

  async loadActive() {
    const database = await getDatabase();
    const id = await database.get('metadata', ACTIVE_RUN_KEY);
    return id ? ((await database.get('runs', id)) ?? null) : null;
  },

  async clearActive() {
    const database = await getDatabase();
    await database.delete('metadata', ACTIVE_RUN_KEY);
  },

  async delete(runId) {
    const database = await getDatabase();
    const transaction = database.transaction(['runs', 'metadata'], 'readwrite');
    const activeId = await transaction.objectStore('metadata').get(ACTIVE_RUN_KEY);
    await Promise.all([
      transaction.objectStore('runs').delete(runId),
      activeId === runId
        ? transaction.objectStore('metadata').delete(ACTIVE_RUN_KEY)
        : Promise.resolve(),
      transaction.done,
    ]);
  },

  async listCompleted() {
    const database = await getDatabase();
    const runs = await database.getAllFromIndex('runs', 'by-phase', 'results');
    return runs.sort((left, right) => right.updatedAt - left.updatedAt);
  },

  async deleteAll() {
    const database = await getDatabase();
    const transaction = database.transaction(['runs', 'metadata'], 'readwrite');
    await Promise.all([
      transaction.objectStore('runs').clear(),
      transaction.objectStore('metadata').clear(),
      transaction.done,
    ]);
  },
};

export class MemoryRunRepository implements RunRepository {
  private runs = new Map<string, RunState>();
  private activeId: string | null = null;

  async save(run: RunState) {
    this.runs.set(run.id, structuredClone(run));
    this.activeId = run.phase === 'results' ? null : run.id;
  }

  async loadActive() {
    const run = this.activeId ? this.runs.get(this.activeId) : null;
    return run ? structuredClone(run) : null;
  }

  async clearActive() {
    this.activeId = null;
  }

  async delete(runId: string) {
    this.runs.delete(runId);
    if (this.activeId === runId) this.activeId = null;
  }

  async listCompleted() {
    return [...this.runs.values()]
      .filter((run) => run.phase === 'results')
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .map((run) => structuredClone(run));
  }

  async deleteAll() {
    this.runs.clear();
    this.activeId = null;
  }
}
